import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { defer as observableDefer, Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { timeoutError } from '../../models/timeout-error';
import { TimeoutLimit } from '../../models/timeout-limit';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';
import { CancellationOrderDetailsResponse } from '../../dtos/response/reservation-cancel-response/cancellation-order-details-response';
import { CancelReservationRequest } from '../../dtos/request/cancel-reservation/cancel-reservation-request';
import { CancelEligibilityRequest } from '../../dtos/request/cancel-reservation/cancel-eligibility-request';
import { CancelResponse } from '../../dtos/response/reservation-cancel-response/cancel-response';

export interface ReservationCancelServiceAPI {
  /**
   * Gets cancellation order details for a given confirmation code
   */
  getCancellationOrderDetails(request: CancelEligibilityRequest): Observable<CancellationOrderDetailsResponse>;

  /**
   * Cancels the reservation
   */
  cancelReservation(request: CancelReservationRequest): Observable<CancelResponse>;
}

@Injectable({
  providedIn: 'root',
})
export class ReservationCancelService implements ReservationCancelServiceAPI {
  private readonly http = inject(HttpClient);
  private readonly eventService = inject(GlobalEventService);

  getCancellationOrderDetails(
    request: CancelEligibilityRequest,
    background: boolean = false
  ): Observable<CancellationOrderDetailsResponse> {
    const options = {
      headers: new HttpHeaders({ background: background.toString() }),
    };
    return this.http.post<CancellationOrderDetailsResponse>(`api/reservation/cancel/order-details`, request, options).pipe(
      timeout({
        each: TimeoutLimit.MEDIUM,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((response) => {
        return response;
      }),
      catchError((err) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
        }
        return observableThrowError(() => err);
      })
    );
  }

  cancelReservation(request: CancelReservationRequest): Observable<CancelResponse> {
    return this.http.put<CancelResponse>(`api/reservation/cancel/process-cancel`, request).pipe(
      timeout({
        each: TimeoutLimit.MEDIUM,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((response) => {
        return response;
      }),
      catchError((err) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
        }
        return observableThrowError(() => err);
      })
    );
  }
}
